import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';

/**
 * Mounts a "Preview" tab on every catalog entity page that carries
 * the `iframe.roadie.so/url` annotation. The actual rendering
 * (allowlist check + iframe wrapping) lives in
 * `IframePreviewContent.tsx` — see the long comment at the top of
 * that file for the security model and the reason we don't depend
 * on the `@roadiehq/backstage-plugin-iframe` package.
 *
 * Why this is a `createFrontendModule({ pluginId: 'catalog' })`
 * instead of a `createFrontendPlugin`:
 *   The extension we register here (`EntityContentBlueprint`) is
 *   consumed by the upstream `page:catalog/entity` page — its
 *   `routableTabs` slot collects every `entity-content` extension
 *   keyed under `pluginId: 'catalog'`. If we registered the tab
 *   under a fresh plugin id, the entity page wouldn't pick it up.
 *
 *   Same trick as `rbacHeaderBridgeModule` in `modules/permissions/`,
 *   which "donates" a header action to the upstream `rbac` plugin.
 *
 * Why the `filter` is a function (not a string expression):
 *   `EntityContentBlueprint`'s `filter` param accepts either a
 *   string DSL (e.g. `'kind: component'`) or a function. The
 *   annotation key `iframe.roadie.so/url` contains a `/`, which
 *   the string DSL parses as a path separator inside the
 *   `metadata.annotations.*` namespace — escaping it produces
 *   subtle bugs that aren't caught at compile time. The function
 *   form is unambiguous: we read the annotation object directly.
 *
 * Why we ALSO check the annotation value isn't blank:
 *   Scaffolder templates often emit annotations conditionally with
 *   `${{ values.previewUrl if values.previewUrl else '' }}`, which
 *   in some YAML serializers ends up as `iframe.roadie.so/url: ""`
 *   instead of being omitted. A blank string would still make the
 *   filter return true if we did a plain key-existence check, so
 *   the tab would render and the content component would render
 *   the "annotation missing" alert — bad UX. Trimming gives us a
 *   single decision point here.
 *
 * Why path: 'preview' (and not 'iframe' or 'roadie-preview'):
 *   The annotation namespace is `iframe.roadie.so/*` for
 *   compatibility with the broader Backstage ecosystem, but the
 *   URL the user sees and bookmarks is internal to our deployment.
 *   `/preview` is short, neutral, and matches the tab title.
 */
const iframePreviewContent = EntityContentBlueprint.make({
  name: 'iframe-preview',
  params: {
    path: 'preview',
    title: 'Preview',
    filter: entity => {
      const value = entity.metadata.annotations?.['iframe.roadie.so/url'];
      return typeof value === 'string' && value.trim().length > 0;
    },
    loader: () =>
      import('./IframePreviewContent').then(m => <m.IframePreviewContent />),
  },
});

export const iframePreviewModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [iframePreviewContent],
});
